/**
 * Route surface.
 *
 * Mirrors docs/design/wireframes/manifest.json — each entry names the sheet or
 * sheets it implements and the PRD requirements it carries. Keep the two in
 * sync: the manifest is the screen inventory of record, and routes.test.ts
 * checks both directions (FR-030, C-002; research §1.4).
 *
 * Session and tenant-status routing is `AuthGate` (FR-021; contracts/
 * session-and-gate.md). Every `/app/*` route mounts under one layout route:
 * `AuthGate` (protect mode) → `AppShell` (which renders the desktop-only notice
 * once, below 768 px, style guide §5.2) → the screen, with `/app` itself
 * redirecting to APP_HOME, the run history (mission 6 T016). The auth routes
 * sit behind the gate too — `/signup` and `/login` inverted, so a fully
 * authenticated verified user visiting them lands in the app, while `/mfa` and
 * `/verify` are protected because they need the partial or unverified session
 * (research §1.13). `/reset` has no gate: request mode needs none, and complete
 * mode must work on a device that still holds a live session so the reset can
 * end every session, that device's included (spec edge case F18).
 *
 * The layout lives here rather than in main.tsx so the tests and the app share
 * one tree; main.tsx hands `routes` to the router unchanged. `routeInventory`
 * is the flat list with `sheet` and `req` on every entry, which the manifest
 * cross-check reads. Every `/app/*` entry carries `handle.crumb`, the
 * breadcrumb segments from register 17, which the shell renders as given.
 *
 * Screens are default exports (post-tasks E2). Frozen after mission 6 WP02
 * (T17): the screen work packages replace their stubs' bodies, never this file.
 *
 * Mission 7 WP08 (T046; FR-025, FR-026, C-002; research §1.19; OR-6): sheets
 * 41, 31 (with its modal, 33) and 32 mount their screens' default exports in
 * place of NotBuilt, and 32 gains its per-run route. The three files start as
 * pending placeholders (`// mission 7 pending:WPnn`) that WP09, WP10 and WP11
 * replace whole. `req` is each sheet's manifest list. The whole tree sits under
 * a pathless root whose errorElement is `RootError`: a throw in AppShell,
 * AuthGate or the credit chip throws again inside the /app errorElement and
 * lands there, outside the shell and the gate. Frozen again after WP08.
 *
 * Mission 8 WP07 (T038; FR-019; research §1.25): `/app/runs/:runId` claims sheet
 * 22, the assistant docked on the run's page (`?assistant=open`, A8), and its
 * `req` gains SAT.1, SAT.5, SAT.7 and SAT.9.
 *
 * Mission 9 WP12 T077 (FR-013, FR-020; TR-8): `/app/subscribe` mounts Subscribe
 * (sheet 08, with CheckoutReturn inside it) in place of NotBuilt.
 *
 * SC-36401: `/` redirects to `/login` in place of NotBuilt until sheet 01
 * (Landing) is built. The entry keeps its sheet and `req` for the manifest
 * cross-check.
 */
import { lazy, type ReactElement } from 'react'
import { Navigate, type Params, type RouteObject } from 'react-router-dom'

import { APP_HOME, AuthGate } from '@/components/trellis/AuthGate'
import { shortId } from '@/lib/format'
import { copyText } from '@/lib/provisional-copy'
import { AppNotFound, AppRouteError } from './AppFallback'
import AppShell from './AppShell'
import Login from './Login'
import Mfa from './Mfa'
import Reset from './Reset'
import RootError from './RootError'
import Signup from './Signup'
import Verify from './Verify'
import Catalog from './rules/Catalog'
import Download from './runs/Download'
import NewRun from './runs/NewRun'
import RunHistory from './runs/RunHistory'
import RunPage from './runs/RunPage'
import Transmit from './runs/Transmit'
import BureauCredentials from './settings/BureauCredentials'
import HeaderParameters from './settings/HeaderParameters'
import Organization from './settings/Organization'
import Billing from './settings/billing/Billing'
import AuditLog from './settings/audit/AuditLog'
import DataRetention from './settings/data/DataRetention'
import Subscribe from './subscribe/Subscribe'
import TransmissionLog from './transmissions/TransmissionLog'

const NotBuilt = lazy(() => import('./NotBuilt'))

/** One breadcrumb segment; every one but the last links. */
export interface CrumbSegment {
  label: string
  to?: string
  /** A run's short id renders in mono (style guide §3). */
  mono?: true
}

export interface RouteHandle {
  crumb: (params: Params) => CrumbSegment[]
}

/** sheet is the wireframe number (or numbers); req lists the PRD requirements the screen carries. */
export type TrellisRoute = RouteObject & {
  sheet?: string | string[]
  req?: string[]
  handle?: RouteHandle
}

/** Protected: no session → /login, pre-MFA → /mfa, unverified → /verify. */
const gate = (screen: ReactElement) => <AuthGate>{screen}</AuthGate>
/** Inverted: a full session leaves for the app (or /verify); anything less renders. */
const invert = (screen: ReactElement) => <AuthGate invert>{screen}</AuthGate>

const RUNS: CrumbSegment = { label: copyText('crumb.runs'), to: '/app/runs' }
const SETTINGS: CrumbSegment = { label: copyText('crumb.settings'), to: '/app/settings/organization' }

function runCrumb(params: Params, linked: boolean): CrumbSegment {
  const id = params['runId'] ?? ''
  const segment: CrumbSegment = { label: shortId(id), mono: true }
  if (linked) segment.to = `/app/runs/${encodeURIComponent(id)}`
  return segment
}

const crumb = (...segments: CrumbSegment[]): RouteHandle => ({ crumb: () => segments })
const leaf = (label: string): CrumbSegment => ({ label })

export const publicRoutes: TrellisRoute[] = [
  // SC-36401: sheet 01 is not built, so `/` replaces itself with /login (no history entry) until it is.
  { path: '/', element: <Navigate to="/login" replace />, sheet: '01', req: ['T.3', 'T.5'] },
  { path: '/pricing', element: <NotBuilt sheet="02" title="Pricing" />, sheet: '02', req: ['B.1', 'B.2', 'B.3', 'T.5'] },
  { path: '/docs/layout/:version', element: <NotBuilt sheet="03" title="Layout spec" />, sheet: '03', req: ['U.1', 'U.3', 'SAT.9'] },
  { path: '/docs/rules', element: <NotBuilt sheet="04" title="Rule catalog (public)" />, sheet: '04', req: ['ANA.7'] },
  { path: '/legal/:doc', element: <NotBuilt sheet="05" title="Legal" />, sheet: '05', req: ['T.1', 'T.6'] },
]

export const authRoutes: TrellisRoute[] = [
  { path: '/signup', element: invert(<Signup />), sheet: '06', req: ['A.1', 'A.2', 'T.1', 'T.5'] },
  { path: '/verify', element: gate(<Verify />), sheet: '07', req: ['A.1', 'NF.9'] },
  { path: '/mfa', element: gate(<Mfa />), sheet: '07', req: ['A.4', 'SEC.7'] },
  { path: '/login', element: invert(<Login />), sheet: '07', req: ['A.5', 'SEC.7'] },
  { path: '/reset', element: <Reset />, sheet: '07', req: ['A.5', 'NF.9'] },
]

/** Every /app/* screen; mounted as children of the /app layout route below. */
export const appRoutes: TrellisRoute[] = [
  {
    path: '/app/subscribe',
    element: <Subscribe />,
    sheet: '08',
    req: ['B.1', 'T.5'],
    handle: crumb(leaf(copyText('crumb.subscribe'))),
  },
  {
    path: '/app/runs',
    element: <RunHistory />,
    sheet: '10',
    req: ['D.5', 'ANA.9', 'B.3', 'GEN.6', 'D.2'],
    handle: crumb(leaf(copyText('crumb.runs'))),
  },
  {
    path: '/app/runs/new',
    element: <NewRun />,
    sheet: ['11', '51'],
    req: ['U.1', 'U.3', 'U.4', 'GEN.3', 'GEN.4', 'GEN.5', 'B.3', 'X.10'],
    handle: crumb(RUNS, leaf(copyText('crumb.newRun'))),
  },
  {
    path: '/app/runs/:runId',
    element: <RunPage />,
    // Mission 8 WP07 T038 (FR-019): sheet 22, the docked assistant, is this route with ?assistant=open (A8).
    sheet: ['12', '13', '20', '21', '22'],
    req: ['U.2', 'GEN.4', 'GEN.5', 'GEN.7', 'D.8', 'ANA.2', 'ANA.4', 'ANA.6', 'T.2', 'SAT.1', 'SAT.5', 'SAT.7', 'SAT.9'],
    handle: { crumb: (params) => [RUNS, runCrumb(params, false)] },
  },
  {
    path: '/app/runs/:runId/report-rejection',
    element: <RunPage mode="report-rejection" />,
    sheet: '23',
    req: ['ANA.11', 'X.1', 'X.9', 'T.3'],
    handle: { crumb: (params) => [RUNS, runCrumb(params, false)] },
  },
  {
    path: '/app/runs/:runId/download',
    element: <Download />,
    sheet: '30',
    req: ['D.1', 'D.2', 'T.2', 'A.5', 'X.10'],
    handle: { crumb: (params) => [RUNS, runCrumb(params, true), leaf(copyText('crumb.download'))] },
  },
  {
    // Sheet 33 is the acknowledgment modal over this route; it has no route of its own (FR-025).
    path: '/app/runs/:runId/transmit',
    element: <Transmit />,
    sheet: ['31', '33'],
    req: [
      'TX.1', 'TX.2', 'TX.3', 'TX.4', 'TX.5', 'TX.6', 'TX.8', 'TX.9', 'T.2', 'X.10', 'A.3', 'A.5', 'NF.9', 'GEN.3',
      'ANA.2', 'ANA.4', 'ANA.6', 'ANA.10', 'SAT.1',
    ],
    handle: { crumb: (params) => [RUNS, runCrumb(params, true), leaf(copyText('crumb.transmit'))] },
  },
  {
    path: '/app/runs/:runId/transmissions',
    element: <TransmissionLog />,
    sheet: '32',
    req: ['TX.6', 'TX.7', 'TX.5', 'TX.8', 'ANA.11', 'A.3', 'A.5', 'D.2', 'D.5', 'J.6', 'NF.9', 'UC-06'],
    handle: { crumb: (params) => [RUNS, runCrumb(params, true), leaf(copyText('crumb.transmissions'))] },
  },
  {
    path: '/app/transmissions',
    element: <TransmissionLog />,
    sheet: '32',
    req: ['TX.6', 'TX.7', 'TX.5', 'TX.8', 'ANA.11', 'A.3', 'A.5', 'D.2', 'D.5', 'J.6', 'NF.9', 'UC-06'],
    handle: crumb(leaf(copyText('crumb.transmissions'))),
  },
  {
    path: '/app/rules',
    element: <Catalog />,
    sheet: '04',
    req: ['ANA.3', 'ANA.4', 'ANA.6', 'ANA.7', 'ANA.9', 'T.2'],
    handle: crumb(leaf(copyText('crumb.ruleCatalog'))),
  },
  {
    path: '/app/settings/organization',
    element: <Organization />,
    sheet: '40',
    req: ['A.2', 'A.4', 'T.1'],
    handle: crumb(SETTINGS, leaf(copyText('crumb.organization'))),
  },
  {
    path: '/app/settings/header',
    element: <HeaderParameters />,
    sheet: '40',
    req: ['GEN.3', 'GEN.6', 'TEN.4', 'D.3', 'A.5'],
    handle: crumb(SETTINGS, leaf(copyText('crumb.headerParameters'))),
  },
  {
    path: '/app/settings/bureaus',
    element: <BureauCredentials />,
    sheet: '41',
    req: ['TX.1', 'TX.2', 'TX.3', 'TX.4', 'TX.9', 'GEN.3', 'D.3', 'A.3', 'A.5', 'X.1', 'X.2', 'O.1'],
    handle: crumb(SETTINGS, leaf(copyText('crumb.bureaus'))),
  },
  {
    path: '/app/settings/data',
    element: <DataRetention />,
    sheet: '43',
    req: ['D.2', 'D.6'],
    handle: crumb(SETTINGS, leaf(copyText('crumb.data'))),
  },
  {
    path: '/app/settings/audit',
    element: <AuditLog />,
    sheet: '44',
    req: ['A.5', 'CMP.5'],
    handle: crumb(SETTINGS, leaf(copyText('crumb.audit'))),
  },
  {
    path: '/app/settings/billing',
    element: <Billing />,
    sheet: '50',
    req: ['B.1', 'B.4', 'B.7'],
    handle: crumb(SETTINGS, leaf(copyText('crumb.billing'))),
  },
]

/**
 * Deliberately absent from GA, drawn in the wireframes and tagged for a later
 * release:
 *   sheet 42 Users & roles  — one identity per tenant means no user list (NFR §4.2)
 *   sheet 45 Codes checklist — O.1, Should-scoped
 */
export const routeInventory: TrellisRoute[] = [...publicRoutes, ...authRoutes, ...appRoutes]

/**
 * The router tree main.tsx mounts. The /app layout's errorElement renders a
 * screen's render error inside the shell, and its last child catches every
 * unknown /app URL, also inside the shell (mission 6 review follow-up 23, A-6).
 * Around everything, a pathless root with no element (it renders its outlet)
 * whose errorElement is `RootError` catches what the /app errorElement cannot:
 * a throw in AppShell, AuthGate or the credit chip, which would throw again
 * inside it (FR-026). None of these is in `routeInventory`: they are not
 * screens of the manifest.
 */
export const routes: TrellisRoute[] = [
  {
    errorElement: <RootError />,
    children: [
      ...publicRoutes,
      ...authRoutes,
      {
        path: '/app',
        element: gate(<AppShell />),
        errorElement: gate(
          <AppShell>
            <AppRouteError />
          </AppShell>,
        ),
        children: [
          { index: true, element: <Navigate to={APP_HOME} replace /> },
          ...appRoutes,
          { path: '*', element: <AppNotFound /> },
        ],
      },
    ],
  },
]
